import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Topbar from '../main/Topbar';
import ProjectSidebar from '../../components/ProjectSidebar';
import { ProjectDetail } from '../../utils/commonUtils';
import { ShopFormUtils } from '../../utils/shopCommon';
import { getAppConfig, updateAppProfilesBulk } from '../../utils/appConfig';
import type { ProfileItem } from '../../utils/projectConfig';
import { getAppDetail, type AppDetail } from '../../utils/app';
import { getMembershipList } from '../../utils/membership';
import { ChevronDown, ChevronUp, RotateCcw, Save } from 'lucide-react';

// 앱 설정 화면 전용 프로필 정의
type ProfileCategory = 'GENERAL' | 'DEFAULT' | 'ETC';

const OPTION_SETS = {
  SIGNUP_METHOD: [
    { value: 'IDPWD', label: '아이디/비밀번호' },
    { value: 'APPLEID', label: 'APPLE ID' },
    { value: 'KAKAO', label: '카카오' },
    { value: 'NAVER', label: '네이버' },
    { value: 'GOOGLE', label: '구글' },
    { value: 'FACEBOOK', label: '페이스북' },
    { value: 'LINE', label: 'LINE' },
  ],
  SIGNUP_ID_VERIFY: [
    { value: 'NONE', label: '없음' },
    { value: 'EMAIL', label: '이메일 형식' },
    { value: 'EMAIL_PIN', label: '이메일 + PIN 코드' },
  ],
  GUEST_MODE: [
    { value: 'DISABLED', label: '이용불가' },
    { value: 'ONBOARDING', label: '온보딩 시작' },
    { value: 'DIRECT', label: '메인화면 시작' },
  ],
} as const;

type ConfigKeyType = 'ENUM_MULTI' | 'ENUM_SINGLE' | 'CODE_REF' | 'STRING';

interface SettingKeyDef {
  settingKey: string;
  label: string;
  type: ConfigKeyType;
  optionSetKey?: keyof typeof OPTION_SETS;
  options?: { value: string; label: string }[];
}

interface ProfileDef {
  category: ProfileCategory;
  label: string;
  settingKeys: SettingKeyDef[];
}

const PROFILES_APP: ProfileDef[] = [
  {
    category: 'GENERAL',
    label: '앱 설정',
    settingKeys: [
      { settingKey: 'SIGNUP_METHOD', label: '고객 등록 방식', type: 'ENUM_MULTI', optionSetKey: 'SIGNUP_METHOD' },
      { settingKey: 'SIGNUP_ID_VERIFY', label: 'ID 검증 방식', type: 'ENUM_SINGLE', optionSetKey: 'SIGNUP_ID_VERIFY' },
      { settingKey: 'GUEST_MODE', label: '비회원 이용', type: 'ENUM_SINGLE', optionSetKey: 'GUEST_MODE' },
    ],
  },
  {
    category: 'DEFAULT',
    label: '기본값',
    settingKeys: [{ settingKey: 'MEMBERSHIP', label: '기본 멤버십', type: 'CODE_REF', options: [] }],
  },
  {
    category: 'ETC',
    label: '기타',
    settingKeys: [{ settingKey: 'NOTE', label: '노트', type: 'STRING' }],
  },
];

type ConfigState = Record<string, string[] | string>;

const getConfigId = (category: ProfileCategory, settingKey: string) => `${category}_${settingKey}`;

const emptyStateApp = (): ConfigState => {
  const initial: ConfigState = {};
  PROFILES_APP.forEach((profile) => {
    profile.settingKeys.forEach((sk) => {
      const id = getConfigId(profile.category, sk.settingKey);
      if (sk.type === 'ENUM_MULTI') initial[id] = [];
      else initial[id] = '';
    });
  });
  return initial;
};

const APP_TYPE_LABELS: Record<string, string> = {
  CUSTOMER: '고객용',
  AGENT: '에이전트',
};

const getAppTypeLabel = (type: string | undefined) =>
  type ? (APP_TYPE_LABELS[type] ?? type) : '—';

const DEFAULT_MEMBERSHIP_CONFIG_ID = getConfigId('DEFAULT', 'MEMBERSHIP');
const SIGNUP_METHOD_CONFIG_ID = getConfigId('GENERAL', 'SIGNUP_METHOD');
const SIGNUP_ID_VERIFY_CONFIG_ID = getConfigId('GENERAL', 'SIGNUP_ID_VERIFY');

interface ChangeItem {
  configId: string;
  category: ProfileCategory;
  categoryLabel: string;
  settingKeyLabel: string;
  settingKey: string;
  keyType: ConfigKeyType;
  from: string;
  to: string;
}

const AppConfig: React.FC = () => {
  const navigate = useNavigate();
  const { projectCode: projectCodeFromUrl, appCode: appCodeFromUrl } = useParams<{
    projectCode: string;
    appCode: string;
  }>();
  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('app-list');

  const [configValues, setConfigValues] = useState<ConfigState>(emptyStateApp);
  const [initialValues, setInitialValues] = useState<ConfigState>(emptyStateApp);
  const [sectionExpanded, setSectionExpanded] = useState<Record<ProfileCategory, boolean>>({
    GENERAL: true,
    DEFAULT: true,
    ETC: true,
  });

  const [saving, setSaving] = useState(false);
  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [membershipOptions, setMembershipOptions] = useState<{ value: string; label: string }[]>([]);
  const [membershipOptionsLoading, setMembershipOptionsLoading] = useState(false);
  const [membershipStatusByCode, setMembershipStatusByCode] = useState<Record<string, string>>({});
  const [appDetail, setAppDetail] = useState<AppDetail | null>(null);

  ShopFormUtils.useMobileSidebarResize(setIsMobileSidebarOpen);

  useEffect(() => {
    if (!projectCodeFromUrl) {
      navigate('/projects', { replace: true });
      return;
    }
    if (!appCodeFromUrl) {
      navigate(`/projects/${projectCodeFromUrl}/apps`, { replace: true });
      return;
    }
    const projectInfo = ShopFormUtils.loadProjectDetail();
    if (!projectInfo) {
      navigate('/projects', { replace: true });
      return;
    }
    const detail: ProjectDetail = {
      id: (projectInfo as { id?: string }).id ?? projectInfo.code ?? '',
      ...projectInfo,
    };
    if (detail.code !== projectCodeFromUrl) {
      navigate('/projects', { replace: true });
      return;
    }
    setProjectDetail(detail);
  }, [projectCodeFromUrl, appCodeFromUrl, navigate]);

  useEffect(() => {
    if (!projectCodeFromUrl || !appCodeFromUrl || !projectDetail) return;
    let cancelled = false;
    getAppDetail(projectCodeFromUrl, appCodeFromUrl)
      .then((d) => {
        if (!cancelled) setAppDetail(d);
      })
      .catch(() => {
        if (!cancelled) setAppDetail(null);
      });
    return () => {
      cancelled = true;
    };
  }, [projectCodeFromUrl, appCodeFromUrl, projectDetail]);

  useEffect(() => {
    if (!projectCodeFromUrl || !appCodeFromUrl || !projectDetail) return;
    const fetchConfig = async () => {
      setConfigLoading(true);
      setConfigError(null);
      try {
        const res = await getAppConfig(projectCodeFromUrl, appCodeFromUrl);
        const next: ConfigState = { ...emptyStateApp() };
        res.items.forEach((item) => {
          let val = item.value;
          for (const profile of PROFILES_APP) {
            const def = profile.settingKeys.find(
              (sk) => getConfigId(profile.category, sk.settingKey) === item.configId
            );
            if (def?.type === 'ENUM_SINGLE' && Array.isArray(val)) {
              val = val.length ? val[0] : '';
              break;
            }
          }
          next[item.configId] = val;
        });
        setConfigValues(next);
        setInitialValues(next);
      } catch (e) {
        setConfigError(e instanceof Error ? e.message : '설정을 불러오지 못했습니다.');
      } finally {
        setConfigLoading(false);
      }
    };
    fetchConfig();
  }, [projectCodeFromUrl, appCodeFromUrl, projectDetail]);

  useEffect(() => {
    if (!projectCodeFromUrl) return;
    let cancelled = false;
    setMembershipOptionsLoading(true);
    getMembershipList(projectCodeFromUrl, 0, 500)
      .then((res) => {
        if (cancelled) return;
        const statusLabel: Record<string, string> = {
          active: '활성',
          inactive: '비활성',
          ended: '종료',
        };
        const typeLabel: Record<string, string> = {
          REWARD: '리워드',
          SUBSCRIPTION: '구독',
          PASS: '패스',
          DISCOUNT: '할인',
        };
        const options = (res?.memberships ?? []).map((m) => ({
          value: m.code,
          label: `${typeLabel[m.type] ?? m.type ?? '-'} | ${statusLabel[m.status] ?? m.status} | ${m.title} | ${m.brandCount}개`,
        }));
        const statusByCode: Record<string, string> = {};
        (res?.memberships ?? []).forEach((m) => {
          statusByCode[m.code] = m.status;
        });
        setMembershipOptions(options);
        setMembershipStatusByCode(statusByCode);
      })
      .catch(() => {
        if (!cancelled) {
          setMembershipOptions([]);
          setMembershipStatusByCode({});
        }
      })
      .finally(() => {
        if (!cancelled) setMembershipOptionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectCodeFromUrl]);

  useEffect(() => {
    if (!saveSuccess) return;
    const t = window.setTimeout(() => setSaveSuccess(false), 3000);
    return () => window.clearTimeout(t);
  }, [saveSuccess]);

  const getOptions = (def: SettingKeyDef) => {
    if (def.optionSetKey && OPTION_SETS[def.optionSetKey]) {
      return OPTION_SETS[def.optionSetKey];
    }
    return def.options ?? [];
  };

  const formatValueForDisplay = (
    sk: SettingKeyDef,
    v: string[] | string,
    opts: { membershipOptions: { value: string; label: string }[] }
  ): string => {
    if (sk.type === 'ENUM_MULTI') {
      const arr = (Array.isArray(v) ? v : [v]).filter(Boolean);
      if (!arr.length) return '—';
      const options = getOptions(sk);
      return arr.map((code) => options.find((o) => o.value === code)?.label ?? code).join(', ');
    }
    if (sk.type === 'ENUM_SINGLE') {
      const code = typeof v === 'string' ? v : (Array.isArray(v) ? v[0] : '');
      if (!code) return '—';
      const options = getOptions(sk);
      return options.find((o) => o.value === code)?.label ?? code;
    }
    if (sk.type === 'CODE_REF') {
      const code = typeof v === 'string' ? v : (Array.isArray(v) ? v[0] : '');
      if (!code) return '—';
      if (sk.settingKey === 'MEMBERSHIP') {
        return opts.membershipOptions.find((o) => o.value === code)?.label ?? code;
      }
      return code;
    }
    return (typeof v === 'string' ? v : '') || '—';
  };

  const getChangeSummary = (): ChangeItem[] => {
    const items: ChangeItem[] = [];
    const opts = { membershipOptions };
    PROFILES_APP.forEach((profile) => {
      profile.settingKeys.forEach((sk) => {
        const configId = getConfigId(profile.category, sk.settingKey);
        const from = initialValues[configId];
        const to = configValues[configId];
        if (JSON.stringify(from) !== JSON.stringify(to)) {
          items.push({
            configId,
            category: profile.category,
            categoryLabel: profile.label,
            settingKeyLabel: sk.label,
            settingKey: sk.settingKey,
            keyType: sk.type,
            from: formatValueForDisplay(sk, from, opts),
            to: formatValueForDisplay(sk, to, opts),
          });
        }
      });
    });
    return items;
  };

  const changedSummary = getChangeSummary();
  const hasChanges = changedSummary.length > 0;
  const isStandaloneProject = projectDetail?.projectType === 'STANDALONE';

  const isSignupIdPwdEnabled = ((configValues[SIGNUP_METHOD_CONFIG_ID] as string[]) ?? []).includes(
    'IDPWD'
  );

  const handleToggleOption = (configId: string, value: string) => {
    setConfigValues((prev) => {
      const current = (prev[configId] as string[]) ?? [];
      const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
      const updated: ConfigState = { ...prev, [configId]: next };

      // 아이디/비밀번호 해제 시 ID 검증 방식 비활성화·초기화
      if (configId === SIGNUP_METHOD_CONFIG_ID && value === 'IDPWD' && !next.includes('IDPWD')) {
        updated[SIGNUP_ID_VERIFY_CONFIG_ID] = 'NONE';
      }

      return updated;
    });
  };

  const handleSingleSelectOption = (configId: string, value: string) => {
    if (configId === SIGNUP_ID_VERIFY_CONFIG_ID && !isSignupIdPwdEnabled) return;
    setConfigValues((prev) => ({ ...prev, [configId]: value }));
  };

  const handleSelectChange = (configId: string, value: string) => {
    if (configId === DEFAULT_MEMBERSHIP_CONFIG_ID && !isStandaloneProject) return;
    setConfigValues((prev) => ({ ...prev, [configId]: value }));
  };

  const handleReset = () => {
    setConfigValues({ ...initialValues });
  };

  const handleSaveAll = async () => {
    if (!projectCodeFromUrl || !appCodeFromUrl || !hasChanges) return;
    setSaving(true);
    setConfigError(null);
    setSaveSuccess(false);
    try {
      const profiles: ProfileItem[] = changedSummary.map((item) => ({
        category: item.category,
        settingKey: item.settingKey,
        settingValue: {
          type: item.keyType,
          value: configValues[item.configId],
        },
      }));
      await updateAppProfilesBulk(projectCodeFromUrl, appCodeFromUrl, profiles);
      setInitialValues({ ...configValues });
      setSaveSuccess(true);
    } catch (e) {
      setConfigError(e instanceof Error ? e.message : '변경사항 저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const toggleSection = (category: ProfileCategory) => {
    setSectionExpanded((prev) => ({ ...prev, [category]: !prev[category] }));
  };

  if (!projectDetail || !appCodeFromUrl) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-gray-500">프로젝트 또는 앱 정보를 불러올 수 없습니다.</p>
          <button
            type="button"
            onClick={() => navigate(projectCodeFromUrl ? `/projects/${projectCodeFromUrl}/apps` : '/projects')}
            className="mt-4 text-blue-600 hover:text-blue-700"
          >
            {projectCodeFromUrl ? '앱 목록으로 돌아가기' : '프로젝트 목록으로 돌아가기'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      <ProjectSidebar
        isDesktopSidebarOpen={isDesktopSidebarOpen}
        setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        projectDetail={projectDetail}
      />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Topbar
          title={appDetail?.name?.trim() || appCodeFromUrl}
          subtitle={
            appCodeFromUrl
              ? `${appCodeFromUrl} · ${getAppTypeLabel(appDetail?.type)}`
              : ''
          }
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          isProfileDropdownOpen={isProfileDropdownOpen}
          setIsProfileDropdownOpen={setIsProfileDropdownOpen}
          showMobileLogo={true}
          projectDetail={projectDetail}
        />

        <main className="flex-1 p-4 lg:p-6 overflow-y-auto">
          <div className="max-w-4xl mx-auto space-y-6">
            {configError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{configError}</div>
            )}
            {saveSuccess && (
              <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
                설정이 저장되었습니다.
              </div>
            )}
            {!configLoading &&
              (() => {
                const defaultMembershipCode = (configValues['DEFAULT_MEMBERSHIP'] as string)?.trim?.() || '';
                const status = defaultMembershipCode ? membershipStatusByCode[defaultMembershipCode] : '';
                const isDefaultMembershipInactive = defaultMembershipCode && status && status !== 'active';
                return isDefaultMembershipInactive ? (
                  <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg flex items-center gap-2">
                    <span className="inline-block w-2 h-2 rounded-full bg-amber-500" />
                    기본 멤버십이 활성 상태가 아니면 실제 서비스 운영 시 문제가 발생할 수 있습니다.
                  </div>
                ) : null;
              })()}
            {configLoading && (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </div>
            )}

            {!configLoading && (
              <>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 lg:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={handleReset}
                        disabled={!hasChanges}
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <RotateCcw className="w-4 h-4" />
                        초기화
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveAll}
                        disabled={!hasChanges || saving}
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {saving ? (
                          <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Save className="w-4 h-4" />
                        )}
                        변경사항 저장
                      </button>
                    </div>
                    {hasChanges && (
                      <p className="text-sm text-amber-600">
                        저장하지 않은 변경사항이 {changedSummary.length}건 있습니다.
                      </p>
                    )}
                  </div>
                  {changedSummary.length > 0 && (
                    <div className="border-t border-gray-200 pt-4">
                      <p className="text-xs font-medium text-gray-500 mb-2">변경된 항목</p>
                      <ul className="space-y-1.5 text-sm">
                        {changedSummary.map((item) => (
                          <li key={item.configId} className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                            <span className="font-medium text-gray-700">
                              [{item.categoryLabel}] {item.settingKeyLabel}
                            </span>
                            <span className="text-gray-500">
                              {item.from} → {item.to}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {PROFILES_APP.map((profile) => {
                  const isExpanded = sectionExpanded[profile.category];
                  const itemCount = profile.settingKeys.length;
                  return (
                    <div
                      key={profile.category}
                      className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
                    >
                      <button
                        type="button"
                        onClick={() => toggleSection(profile.category)}
                        className="w-full flex items-center justify-between px-4 lg:px-6 py-4 text-left hover:bg-gray-50 transition-colors"
                      >
                        <span className="text-lg font-semibold text-gray-900">{profile.label}</span>
                        <span className="flex items-center gap-2">
                          <span className="text-sm font-normal text-gray-500">{itemCount}개 항목</span>
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5 text-gray-500" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-500" />
                          )}
                        </span>
                      </button>
                      {isExpanded && (
                        <div className="border-t border-gray-200 px-4 lg:px-6 py-4 space-y-4">
                          {profile.settingKeys.map((sk) => {
                            const configId = getConfigId(profile.category, sk.settingKey);
                            const options = getOptions(sk);

                            return (
                              <div
                                key={configId}
                                className="flex flex-col sm:flex-row sm:items-center gap-3 py-3 border-b border-gray-100 last:border-0"
                              >
                                <div className="w-52 sm:w-56 flex-shrink-0">
                                  <p className="text-sm font-medium text-gray-700">{sk.label}</p>
                                  <p className="text-xs text-gray-500 mt-0.5">{sk.settingKey}</p>
                                </div>
                                <div className="flex-1 flex flex-wrap items-center gap-2 justify-start text-left min-w-0">
                                  {sk.type === 'ENUM_MULTI' && (
                                    <div className="flex flex-wrap gap-2 justify-start">
                                      {options.map((opt) => {
                                        const isOn = ((configValues[configId] as string[]) ?? []).includes(opt.value);
                                        return (
                                          <button
                                            key={opt.value}
                                            type="button"
                                            onClick={() => handleToggleOption(configId, opt.value)}
                                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                              isOn
                                                ? 'bg-blue-600 text-white hover:bg-blue-700'
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                          >
                                            <span
                                              className={`w-2 h-2 rounded-full ${isOn ? 'bg-white' : 'bg-gray-400'}`}
                                            />
                                            {opt.label}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  )}
                                  {sk.type === 'ENUM_SINGLE' && (
                                    <div className="flex flex-col gap-2 justify-start">
                                      <div
                                        className={`flex flex-wrap gap-2 justify-start ${
                                          configId === SIGNUP_ID_VERIFY_CONFIG_ID && !isSignupIdPwdEnabled
                                            ? 'opacity-50 pointer-events-none'
                                            : ''
                                        }`}
                                      >
                                        {options.map((opt) => {
                                          const current = configValues[configId];
                                          const selected =
                                            typeof current === 'string'
                                              ? current === opt.value
                                              : (Array.isArray(current) ? current[0] : '') === opt.value;
                                          const disabled =
                                            configId === SIGNUP_ID_VERIFY_CONFIG_ID && !isSignupIdPwdEnabled;
                                          return (
                                            <button
                                              key={opt.value}
                                              type="button"
                                              disabled={disabled}
                                              onClick={() => handleSingleSelectOption(configId, opt.value)}
                                              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors disabled:cursor-not-allowed ${
                                                selected
                                                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                              }`}
                                            >
                                              <span
                                                className={`w-2 h-2 rounded-full ${selected ? 'bg-white' : 'bg-gray-400'}`}
                                              />
                                              {opt.label}
                                            </button>
                                          );
                                        })}
                                      </div>
                                      {configId === SIGNUP_ID_VERIFY_CONFIG_ID && !isSignupIdPwdEnabled && (
                                        <p className="text-xs text-gray-500">
                                          고객 등록 방식에서 &quot;아이디/비밀번호&quot;를 선택하면 설정할 수 있습니다.
                                        </p>
                                      )}
                                    </div>
                                  )}
                                  {sk.type === 'STRING' && (
                                    <textarea
                                      value={(configValues[configId] as string) ?? ''}
                                      onChange={(e) => handleSelectChange(configId, e.target.value)}
                                      placeholder="입력하세요"
                                      rows={4}
                                      className="block w-full min-w-[200px] px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-y"
                                    />
                                  )}
                                  {sk.type === 'CODE_REF' && (
                                    <select
                                      value={(configValues[configId] as string) ?? ''}
                                      onChange={(e) => handleSelectChange(configId, e.target.value)}
                                      disabled={
                                        membershipOptionsLoading ||
                                        (configId === DEFAULT_MEMBERSHIP_CONFIG_ID && !isStandaloneProject)
                                      }
                                      className="block w-full min-w-[200px] sm:w-auto px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm text-left disabled:bg-gray-100"
                                    >
                                      <option value="">
                                        {membershipOptionsLoading ? '멤버십 목록 로딩 중...' : '멤버십을 선택하세요'}
                                      </option>
                                      {membershipOptions.map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                          {opt.label}
                                        </option>
                                      ))}
                                    </select>
                                  )}
                                  {sk.type === 'CODE_REF' &&
                                    configId === DEFAULT_MEMBERSHIP_CONFIG_ID &&
                                    !isStandaloneProject && (
                                      <p className="text-xs text-amber-600">
                                        기본 멤버십은 프로젝트 타입이 <strong>단독</strong>일 때만 설정할 수 있습니다.
                                      </p>
                                    )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AppConfig;
